import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import PhoneInput from '../../components/ui/PhoneInput';
import { WebsiteContact } from '../../components/website/WebsiteTemplate';
import { apiService } from '../../services/api';

interface ContactPageProps {
  title: string;
  labels: {
    fullName: string;
    phone: string;
    email: string;
    message: string;
    submit: string;
    success: string;
    submitError: string;
  };
  contact: WebsiteContact;
}

const ContactPage: React.FC<ContactPageProps> = ({ title, labels, contact }) => {
  const [searchParams] = useSearchParams();
  const [formStartedAt] = useState(() => new Date().toISOString());
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitted(false);
    setSubmitError(null);

    try {
      await apiService.post('/website/leads/public', {
        full_name: fullName,
        phone,
        email: email || null,
        message: message || null,
        intent: searchParams.get('intent') || 'general_contact',
        vehicle_id: searchParams.get('vehicleId') || null,
        website: website || null,
        submitted_at: formStartedAt,
      });

      setSubmitted(true);
      setFullName('');
      setPhone('');
      setEmail('');
      setMessage('');
    } catch {
      setSubmitError(labels.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{title}</h1>

          <div className="space-y-3 text-gray-700">
            <p>
              <span className="font-semibold">Phone:</span> {contact.phone}
            </p>
            <p>
              <span className="font-semibold">Email:</span> {contact.email}
            </p>
            <p>
              <span className="font-semibold">Address:</span> {contact.address}
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-8">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <input
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={labels.fullName}
              required
            />

            <PhoneInput
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={phone}
              onChange={setPhone}
              placeholder={labels.phone}
              required
            />

            <input
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={labels.email}
            />

            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-28"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={labels.message}
            />

            <input
              className="hidden"
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center rounded-md bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {isSubmitting ? '...' : labels.submit}
            </button>
          </form>

          {submitted && <p className="mt-4 text-sm font-medium text-green-700">{labels.success}</p>}
          {submitError && <p className="mt-4 text-sm font-medium text-red-700">{submitError}</p>}
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
